/**
 * Unified authenticated fetch wrapper — use getApiBase() and shared session handling.
 */
import { apiUrl } from './apiBase';
import { formatApiErrorDetail } from './formatApiError';
import { checkResponseSessionEnd } from './sessionExpired';
import { toastError, toastSuccess } from './swotifyToast';

export function getSwotifyAuthHeaders(extra = {}) {
  const token = localStorage.getItem('swotify_access_token');
  const schoolCode =
    localStorage.getItem('swotify_school_code') ||
    localStorage.getItem('swotify_active_school_code');
  const headers = {
    Accept: 'application/json',
    ...extra,
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (schoolCode) headers['X-School-Code'] = schoolCode;
  return headers;
}

export async function swotifyFetch(path, options = {}) {
  const {
    json = true,
    headers: extraHeaders,
    signal,
    successMessage,
    errorMessage,
    showErrorToast = false,
    silent = false,
    ...init
  } = options;

  const headers = getSwotifyAuthHeaders(extraHeaders);
  if (json && init.body && !(init.body instanceof FormData) && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  try {
    const response = await fetch(apiUrl(path), {
      credentials: 'include',
      ...init,
      headers,
      signal,
    });
    if (!response.ok) {
      void checkResponseSessionEnd(response);
      const payload = await response.json().catch(() => ({}));
      const detail = payload?.detail ?? payload?.message ?? payload?.error_message ?? payload;
      throw new Error(
        formatApiErrorDetail(detail, `Request failed (HTTP ${response.status})`),
      );
    }
    if (response.status === 204) {
      if (!silent && successMessage) toastSuccess(successMessage);
      return null;
    }
    const contentType = response.headers.get('content-type') || '';
    const result = contentType.includes('application/json')
      ? await response.json()
      : await response.text();
    if (!silent && successMessage) toastSuccess(successMessage);
    return result;
  } catch (err) {
    if (!silent && (errorMessage || showErrorToast)) {
      toastError(errorMessage || err?.message || 'Request failed');
    }
    throw err;
  }
}

/** @deprecated use getSwotifyAuthHeaders */
export function getAuthHeaders(extra = {}) {
  return getSwotifyAuthHeaders(extra);
}

/** @deprecated use apiUrl from apiBase */
export function getApiBaseUrl() {
  return import('./apiBase.js').then((m) => m.getApiBase());
}
